import React, {useRef, useEffect} from 'react';
import classNames from 'classnames';

interface IProps {
  steps: string[];
  currentStep: number; // starts from 1
  className?: string;
}

const MultiStepComponent = ({steps, currentStep, className}: IProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Initialize the ref array with the correct length
  if (stepRefs.current.length !== steps.length) {
    stepRefs.current = Array(steps.length).fill(null);
  }

  // Convert currentStep (1-based) to index (0-based)
  const currentIndex = currentStep - 1;

  useEffect(() => {
    // Scroll to the current step when it changes
    if (stepRefs.current[currentIndex] && containerRef.current) {
      const container = containerRef.current;
      const activeStep = stepRefs.current[currentIndex];

      if (!activeStep) return;

      // Calculate the scroll position
      const scrollLeft =
        activeStep.offsetLeft -
        container.offsetWidth / 2 +
        activeStep.offsetWidth / 2;

      // Smooth scroll to the position
      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    }
  }, [currentIndex]);

  return (
    <div
      ref={containerRef}
      className={classNames(
        'relative flex items-center overflow-x-auto gap-4 pb-2 no-scrollbar shrink-0',
        className,
      )}
    >
      {steps.map((stepName, index) => (
        <Step
          key={index}
          name={stepName}
          isCurrent={index === currentIndex}
          isPast={index < currentIndex}
          ref={el => (stepRefs.current[index] = el)}
        />
      ))}

      <div className="absolute z-0 top-[7px] right-5 left-5 border-dotted border-[1px] w-[100%]" />
    </div>
  );
};

// ----------------------------------------------------------------------

interface StepProps {
  name: string;
  isCurrent: boolean;
  isPast: boolean;
}

const Step = React.forwardRef<HTMLDivElement, StepProps>(
  ({name, isCurrent, isPast}, ref) => {
    return (
      <div
        ref={ref}
        className={`relative z-[10] flex flex-col items-center justify-center gap-2 shrink-0`}
      >
        <div
          className={`w-4 h-4 rounded-full border-2  ${
            isCurrent ? 'border-farm-shop-500' : 'border-gray-500'
          } ${isPast ? 'bg-farm-shop-300 !border-farm-shop-300' : 'bg-white'}`}
        />

        <span
          className={`text-sm font-Medium ${
            isCurrent ? 'text-farm-shop-500' : 'text-gray-500'
          } ${isPast && '!text-farm-shop-300'}`}
        >
          {name}
        </span>
      </div>
    );
  },
);

export default MultiStepComponent;
